import { useState, useEffect } from 'react';
import { messagesAPI, noticesAPI, classesAPI, bookingsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Toaster, toast } from 'sonner';
import { 
  Mail, Bell, Send, Plus, Users, BookOpen, User, 
  Eye, Clock, Trash2, Pencil, AlertCircle, Info, CheckCircle, RefreshCw, 
  Check, X, ChevronRight, MailWarning, Loader2
} from 'lucide-react';
import Navbar from '../components/Navbar';

const AdminMessaging = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [notices, setNotices] = useState([]);
  const [classes, setClasses] = useState([]);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resendingId, setResendingId] = useState(null);

  // Export emails state
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportScope, setExportScope] = useState('class'); // 'all' or 'class'
  const [exportClassIds, setExportClassIds] = useState([]);
  const [exportIncludeG2, setExportIncludeG2] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportResult, setExportResult] = useState(null);

  const handleExportEmails = async () => {
    setExportLoading(true);
    try {
      const payload = {
        recipient_type: exportScope,
        include_guardian2: exportIncludeG2,
      };
      if (exportScope === 'class') {
        if (exportClassIds.length === 0) {
          toast.error('Pick at least one class');
          setExportLoading(false);
          return;
        }
        payload.recipient_class_ids = exportClassIds;
      }
      const res = await messagesAPI.exportRecipients(payload);
      setExportResult(res.data);
    } catch (err) {
      toast.error('Export failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExportLoading(false);
    }
  };

  const copyEmails = async () => {
    if (!exportResult?.emails?.length) return;
    try {
      await navigator.clipboard.writeText(exportResult.emails.join(', '));
      toast.success(`${exportResult.count} email(s) copied — paste into BCC`);
    } catch {
      toast.error('Browser blocked clipboard access — select the textarea and copy manually');
    }
  };

  const handleResendUnread = async (msg, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const unreadCount = (msg.recipient_user_ids?.length || 0) - (msg.read_by?.length || 0);
    if (unreadCount === 0) {
      toast.info('Everyone has already read this message');
      return;
    }
    if (!window.confirm(`Re-send this message by email to the ${unreadCount} parent${unreadCount === 1 ? '' : 's'} who haven't read it yet?`)) return;
    setResendingId(msg.id);
    try {
      const res = await messagesAPI.resendUnread(msg.id);
      const { sent, failed } = res.data || {};
      if (sent > 0) {
        toast.success(`Re-sent to ${sent} parent${sent === 1 ? '' : 's'}${failed ? ` (${failed} failed)` : ''}`);
      } else {
        toast.warning(`Nothing sent (${failed || 0} failed)`);
      }
      fetchData();
    } catch (err) {
      toast.error('Resend failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setResendingId(null);
    }
  };
  
  // Modal states
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [noticeModalOpen, setNoticeModalOpen] = useState(false);
  const [editingNoticeId, setEditingNoticeId] = useState(null);
  const [viewMessageModal, setViewMessageModal] = useState(null);
  
  // Form states
  const [messageForm, setMessageForm] = useState({
    subject: '',
    content: '',
    recipient_type: 'all',
    recipient_class_ids: [],
    recipient_user_ids: []
  });
  
  const [noticeForm, setNoticeForm] = useState({
    title: '',
    content: '',
    priority: 'normal',
    expires_at: ''
  });
  
  const [sending, setSending] = useState(false);

  // Read receipts state
  const [receiptsData, setReceiptsData] = useState(null);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptsOpen, setReceiptsOpen] = useState(false);

  const handleViewReceipts = async (id, type = 'notice') => {
    setReceiptsOpen(true);
    setReceiptsLoading(true);
    try {
      const res = type === 'message'
        ? await messagesAPI.readReceipts(id)
        : await noticesAPI.readReceipts(id);
      setReceiptsData(res.data);
    } catch (err) {
      toast.error('Failed to load read receipts');
      setReceiptsOpen(false);
    } finally {
      setReceiptsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Auto-refresh when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also set up periodic refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, []);

  const fetchData = async () => {
    try {
      const [messagesRes, noticesRes, classesRes, bookingsRes] = await Promise.all([
        messagesAPI.getAll(),
        noticesAPI.getAll(),
        classesAPI.getAll(),
        bookingsAPI.getAll()
      ]);
      setMessages(messagesRes.data);
      setNotices(noticesRes.data);
      setClasses(classesRes.data);
      
      // Extract unique parents from bookings
      const uniqueParents = {};
      bookingsRes.data.forEach(b => {
        if (!uniqueParents[b.user_id]) {
          uniqueParents[b.user_id] = {
            id: b.user_id,
            student_name: b.student_name
          };
        }
      });
      setParents(Object.values(uniqueParents));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageForm.subject.trim() || !messageForm.content.trim()) {
      toast.error('Please fill in subject and content');
      return;
    }
    
    setSending(true);
    try {
      const res = await messagesAPI.send(messageForm);
      toast.success(`Message sent to ${res.data.recipients_count} recipients`);
      setMessageModalOpen(false);
      setMessageForm({
        subject: '',
        content: '',
        recipient_type: 'all',
        recipient_class_ids: [],
        recipient_user_ids: []
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleCreateNotice = async () => {
    if (!noticeForm.title.trim() || !noticeForm.content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }
    
    setSending(true);
    try {
      if (editingNoticeId) {
        await noticesAPI.update(editingNoticeId, noticeForm);
        toast.success('Notice updated');
      } else {
        await noticesAPI.create(noticeForm);
        toast.success('Notice published');
      }
      setNoticeModalOpen(false);
      setEditingNoticeId(null);
      setNoticeForm({ title: '', content: '', priority: 'normal', expires_at: '' });
      fetchData();
    } catch (error) {
      toast.error(editingNoticeId ? 'Failed to update notice' : 'Failed to create notice');
    } finally {
      setSending(false);
    }
  };

  const handleEditNotice = (notice) => {
    setEditingNoticeId(notice.id);
    setNoticeForm({
      title: notice.title || '',
      content: notice.content || '',
      priority: notice.priority || 'normal',
      // <input type="date"> expects YYYY-MM-DD; trim any ISO time portion.
      expires_at: notice.expires_at ? String(notice.expires_at).slice(0, 10) : '',
    });
    setNoticeModalOpen(true);
  };

  const openNewNoticeModal = () => {
    setEditingNoticeId(null);
    setNoticeForm({ title: '', content: '', priority: 'normal', expires_at: '' });
    setNoticeModalOpen(true);
  };

  const handleDeleteNotice = async (noticeId) => {
    if (!confirm('Delete this notice?')) return;
    try {
      await noticesAPI.delete(noticeId);
      toast.success('Notice deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete notice');
    }
  };

  const toggleClassSelection = (classId) => {
    setMessageForm(prev => ({
      ...prev,
      recipient_class_ids: prev.recipient_class_ids.includes(classId)
        ? prev.recipient_class_ids.filter(id => id !== classId)
        : [...prev.recipient_class_ids, classId]
    }));
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700';
      case 'important': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-pink-50 to-white">
      <Navbar />
      <Toaster position="top-center" richColors />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Mail className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-neutral-900">
                Communications
              </h1>
              <p className="text-neutral-600">Send messages and post notices to parents</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <Tabs defaultValue="messages">
          <TabsList className="mb-6">
            <TabsTrigger value="messages">
              <Mail className="w-4 h-4 mr-2" />
              Messages
            </TabsTrigger>
            <TabsTrigger value="notices">
              <Bell className="w-4 h-4 mr-2" />
              Noticeboard
            </TabsTrigger>
          </TabsList>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-lg font-semibold">Sent Messages</h2>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setExportModalOpen(true);
                    setExportResult(null);
                  }}
                  className="border-orange-400 text-orange-600 hover:bg-orange-50"
                  data-testid="export-emails-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Export Emails
                </Button>
              <Dialog open={messageModalOpen} onOpenChange={setMessageModalOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    New Message
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Send Message to Parents</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Recipients</Label>
                      <Select 
                        value={messageForm.recipient_type} 
                        onValueChange={(v) => setMessageForm({...messageForm, recipient_type: v, recipient_class_ids: [], recipient_user_ids: []})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              All Parents
                            </div>
                          </SelectItem>
                          <SelectItem value="class">
                            <div className="flex items-center gap-2">
                              <BookOpen className="w-4 h-4" />
                              By Class
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {messageForm.recipient_type === 'class' && (
                      <div>
                        <Label className="mb-2 block">Select Classes</Label>
                        <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg">
                          {classes.map(cls => (
                            <label key={cls.id} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-neutral-50 rounded">
                              <Checkbox 
                                checked={messageForm.recipient_class_ids.includes(cls.id)}
                                onCheckedChange={() => toggleClassSelection(cls.id)}
                              />
                              <span className="text-sm">{cls.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <Label>Subject</Label>
                      <Input
                        value={messageForm.subject}
                        onChange={(e) => setMessageForm({...messageForm, subject: e.target.value})}
                        placeholder="Message subject..."
                      />
                    </div>

                    <div>
                      <Label>Message</Label>
                      <Textarea
                        value={messageForm.content}
                        onChange={(e) => setMessageForm({...messageForm, content: e.target.value})}
                        placeholder="Type your message here..."
                        rows={6}
                      />
                    </div>

                    <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                      <Info className="w-4 h-4 inline mr-2" />
                      This message will be sent to parents' email addresses and stored in their portal inbox.
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setMessageModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleSendMessage} disabled={sending}>
                      <Send className="w-4 h-4 mr-2" />
                      {sending ? 'Sending...' : 'Send Message'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            {/* Export Emails Modal */}
            <Dialog open={exportModalOpen} onOpenChange={(o) => { setExportModalOpen(o); if (!o) setExportResult(null); }}>
              <DialogContent className="max-w-2xl" data-testid="export-emails-modal">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-orange-500" />
                    Export Parent Emails
                  </DialogTitle>
                  <p className="text-sm text-neutral-500 mt-1">
                    Pick a scope, get a clean comma-separated list of parent emails to paste into Gmail/Outlook BCC.
                  </p>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {!exportResult ? (
                    <>
                      <div>
                        <Label>Scope</Label>
                        <Select value={exportScope} onValueChange={setExportScope}>
                          <SelectTrigger data-testid="export-scope-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All parents (everyone with a portal account)</SelectItem>
                            <SelectItem value="class">Parents in specific classes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {exportScope === 'class' && (
                        <div>
                          <Label>Classes</Label>
                          <div className="mt-1 border rounded-lg max-h-64 overflow-y-auto p-1 bg-white">
                            {classes.map(c => (
                              <label
                                key={c.id}
                                className="flex items-center gap-2 p-2 hover:bg-neutral-50 rounded cursor-pointer"
                                data-testid={`export-class-${c.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={exportClassIds.includes(c.id)}
                                  onChange={() => setExportClassIds(prev =>
                                    prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id]
                                  )}
                                />
                                <span className="text-sm">
                                  <span className="font-medium">{c.name}</span>
                                  {c.day_of_week && <span className="text-neutral-500 ml-2">{c.day_of_week}</span>}
                                </span>
                              </label>
                            ))}
                          </div>
                          {exportClassIds.length > 0 && (
                            <p className="text-xs text-neutral-500 mt-1">{exportClassIds.length} class(es) selected</p>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="include-g2"
                          checked={exportIncludeG2}
                          onCheckedChange={setExportIncludeG2}
                        />
                        <Label htmlFor="include-g2" className="cursor-pointer text-sm">
                          Include secondary guardian (Guardian 2) emails when available
                        </Label>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                        <p className="text-orange-800 font-medium">
                          {exportResult.count} email{exportResult.count === 1 ? '' : 's'} ready
                        </p>
                        <p className="text-orange-700 text-xs mt-1">
                          Click "Copy all" → open Gmail/Outlook → paste into BCC field.
                        </p>
                      </div>
                      <div>
                        <Label>Comma-separated list (BCC-ready)</Label>
                        <Textarea
                          readOnly
                          value={exportResult.emails.join(', ')}
                          className="mt-1 font-mono text-xs h-32"
                          data-testid="export-emails-textarea"
                          onFocus={(e) => e.target.select()}
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto border rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-neutral-50 text-xs text-neutral-500 sticky top-0">
                            <tr>
                              <th className="text-left p-2">Email</th>
                              <th className="text-left p-2">Name</th>
                              <th className="text-left p-2">Student(s)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {exportResult.rows.map((r, i) => (
                              <tr key={i} className="border-t">
                                <td className="p-2 font-mono text-xs">{r.email}</td>
                                <td className="p-2">{r.name || '—'}</td>
                                <td className="p-2 text-neutral-500">{r.student_names.join(', ') || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
                <DialogFooter className="gap-2">
                  {exportResult ? (
                    <>
                      <Button variant="outline" onClick={() => setExportResult(null)}>Back</Button>
                      <Button onClick={copyEmails} className="bg-orange-500 hover:bg-orange-600" data-testid="copy-emails-btn">
                        <Mail className="w-4 h-4 mr-2" />
                        Copy all {exportResult.count}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={() => setExportModalOpen(false)}>Cancel</Button>
                      <Button onClick={handleExportEmails} disabled={exportLoading} data-testid="export-fetch-btn">
                        {exportLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fetching…</> : 'Get emails'}
                      </Button>
                    </>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <div className="space-y-4">
              {messages.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Mail className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                    <p className="text-neutral-500">No messages sent yet</p>
                  </CardContent>
                </Card>
              ) : (
                messages.map(msg => {
                  const readCount = msg.read_by?.length || 0;
                  const totalRecipients = msg.recipient_user_ids?.length || 0;
                  const unreadCount = totalRecipients - readCount;
                  
                  return (
                    <Card 
                      key={msg.id} 
                      className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => setViewMessageModal(msg)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-neutral-900">{msg.subject}</h3>
                            <p className="text-sm text-neutral-600 mt-1 line-clamp-2">{msg.content}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-neutral-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(msg.created_at).toLocaleString()}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {totalRecipients} recipients
                              </span>
                              <span className="flex items-center gap-1 text-green-600">
                                <Check className="w-3 h-3" />
                                {readCount} read
                              </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleViewReceipts(msg.id, 'message'); }}
                                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                data-testid={`msg-read-receipts-${msg.id}`}
                              >
                                <Eye className="w-3.5 h-3.5" />
                                see who
                              </button>
                              {unreadCount > 0 && (
                                <span className="flex items-center gap-1 text-orange-500">
                                  <X className="w-3 h-3" />
                                  {unreadCount} unread
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Badge variant="secondary">
                              {msg.recipient_type === 'all' ? 'All Parents' : 
                               msg.recipient_type === 'class' ? 'By Class' : 'Individual'}
                            </Badge>
                            {unreadCount > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => handleResendUnread(msg, e)}
                                disabled={resendingId === msg.id}
                                className="border-orange-400 text-orange-600 hover:bg-orange-50"
                                data-testid={`resend-unread-${msg.id}`}
                                title={`Re-send email to the ${unreadCount} unread recipient${unreadCount === 1 ? '' : 's'}`}
                              >
                                {resendingId === msg.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <><MailWarning className="w-3 h-3 mr-1" /> Re-send ({unreadCount})</>
                                )}
                              </Button>
                            )}
                            <ChevronRight className="w-5 h-5 text-neutral-400" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
            
            {/* Message Detail Modal */}
            <Dialog open={!!viewMessageModal} onOpenChange={(open) => !open && setViewMessageModal(null)}>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                {viewMessageModal && (
                  <>
                    <DialogHeader>
                      <DialogTitle>{viewMessageModal.subject}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="text-sm text-neutral-500">
                        Sent by {viewMessageModal.sender_name} • {new Date(viewMessageModal.created_at).toLocaleString()}
                      </div>
                      <div className="bg-neutral-50 p-4 rounded-lg whitespace-pre-wrap">
                        {viewMessageModal.content}
                      </div>
                      
                      {/* Recipients List */}
                      <div className="border-t pt-4">
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Recipients ({viewMessageModal.recipient_user_ids?.length || 0})
                        </h4>
                        
                        {viewMessageModal.recipient_details?.length > 0 ? (
                          <div className="space-y-2 max-h-60 overflow-y-auto">
                            {viewMessageModal.recipient_details.map((recipient, idx) => {
                              const hasRead = viewMessageModal.read_by?.includes(recipient.user_id);
                              return (
                                <div 
                                  key={idx}
                                  className={`flex items-center justify-between p-3 rounded-lg ${
                                    hasRead ? 'bg-green-50 border border-green-100' : 'bg-orange-50 border border-orange-100'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                      hasRead ? 'bg-green-100' : 'bg-orange-100'
                                    }`}>
                                      {hasRead ? (
                                        <Check className="w-4 h-4 text-green-600" />
                                      ) : (
                                        <X className="w-4 h-4 text-orange-500" />
                                      )}
                                    </div>
                                    <div>
                                      <p className="font-medium text-sm">{recipient.name || 'Unknown'}</p>
                                      <p className="text-xs text-neutral-500">{recipient.email}</p>
                                      {recipient.student_name && (
                                        <p className="text-xs text-neutral-400">Parent of {recipient.student_name}</p>
                                      )}
                                    </div>
                                  </div>
                                  <Badge className={hasRead ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}>
                                    {hasRead ? 'Read' : 'Unread'}
                                  </Badge>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-neutral-500">
                            Recipient details not available for older messages.
                            <br />
                            <span className="text-green-600">{viewMessageModal.read_by?.length || 0} read</span>
                            {' • '}
                            <span className="text-orange-500">
                              {(viewMessageModal.recipient_user_ids?.length || 0) - (viewMessageModal.read_by?.length || 0)} unread
                            </span>
                          </p>
                        )}
                      </div>
                    </div>
                    <DialogFooter className="gap-2 flex-wrap">
                      {(() => {
                        const unread = (viewMessageModal.recipient_user_ids?.length || 0) - (viewMessageModal.read_by?.length || 0);
                        if (unread <= 0) return null;
                        return (
                          <Button
                            variant="outline"
                            onClick={() => handleResendUnread(viewMessageModal)}
                            disabled={resendingId === viewMessageModal.id}
                            className="border-orange-400 text-orange-600 hover:bg-orange-50"
                            data-testid="resend-unread-modal"
                          >
                            {resendingId === viewMessageModal.id ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Re-sending…</>
                            ) : (
                              <><MailWarning className="w-4 h-4 mr-2" /> Re-send email to {unread} unread</>
                            )}
                          </Button>
                        );
                      })()}
                      <Button variant="outline" onClick={() => setViewMessageModal(null)}>Close</Button>
                    </DialogFooter>
                  </>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Noticeboard Tab */}
          <TabsContent value="notices">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-lg font-semibold">Noticeboard</h2>
              <Dialog
                open={noticeModalOpen}
                onOpenChange={(open) => {
                  setNoticeModalOpen(open);
                  if (!open) {
                    setEditingNoticeId(null);
                    setNoticeForm({ title: '', content: '', priority: 'normal', expires_at: '' });
                  }
                }}
              >
                <Button onClick={openNewNoticeModal} data-testid="new-notice-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Notice
                </Button>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingNoticeId ? 'Edit Notice' : 'Create Notice'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={noticeForm.title}
                        onChange={(e) => setNoticeForm({...noticeForm, title: e.target.value})}
                        placeholder="Notice title..."
                        data-testid="notice-title-input"
                      />
                    </div>

                    <div>
                      <Label>Priority</Label>
                      <Select 
                        value={noticeForm.priority} 
                        onValueChange={(v) => setNoticeForm({...noticeForm, priority: v})}
                      >
                        <SelectTrigger data-testid="notice-priority-trigger">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="important">Important</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Content</Label>
                      <Textarea
                        value={noticeForm.content}
                        onChange={(e) => setNoticeForm({...noticeForm, content: e.target.value})}
                        placeholder="Notice content..."
                        rows={4}
                        data-testid="notice-content-input"
                      />
                    </div>

                    <div>
                      <Label>Expires (optional)</Label>
                      <Input
                        type="date"
                        value={noticeForm.expires_at}
                        onChange={(e) => setNoticeForm({...noticeForm, expires_at: e.target.value})}
                        data-testid="notice-expires-input"
                      />
                    </div>

                    {editingNoticeId && (
                      <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                        Editing an existing notice will NOT re-send the email to parents — your edits update the noticeboard only.
                      </p>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setNoticeModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateNotice} disabled={sending} data-testid="notice-submit-btn">
                      {sending
                        ? (editingNoticeId ? 'Saving...' : 'Publishing...')
                        : (editingNoticeId ? 'Save Changes' : 'Publish Notice')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {notices.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Bell className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                    <p className="text-neutral-500">No notices yet</p>
                  </CardContent>
                </Card>
              ) : (
                notices.map(notice => (
                  <Card key={notice.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {notice.priority === 'urgent' ? (
                            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                          ) : notice.priority === 'important' ? (
                            <Info className="w-5 h-5 text-yellow-500 mt-0.5" />
                          ) : (
                            <Bell className="w-5 h-5 text-blue-500 mt-0.5" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{notice.title}</h3>
                              <Badge className={getPriorityColor(notice.priority)}>
                                {notice.priority}
                              </Badge>
                            </div>
                            <p className="text-sm text-neutral-600 mt-1 whitespace-pre-wrap">{notice.content}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-neutral-500 flex-wrap">
                              <span>Posted: {new Date(notice.created_at).toLocaleDateString('en-GB')}</span>
                              <button
                                onClick={() => handleViewReceipts(notice.id)}
                                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                data-testid={`read-receipts-${notice.id}`}
                              >
                                <Eye className="w-3.5 h-3.5" />
                                {notice.read_by?.length || 0} read — see who
                              </button>
                              {notice.expires_at && (
                                <span>Expires: {new Date(notice.expires_at).toLocaleDateString('en-GB')}</span>
                              )}
                              {notice.updated_at && (
                                <span className="italic">Edited {new Date(notice.updated_at).toLocaleDateString('en-GB')}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditNotice(notice)}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            data-testid={`edit-notice-${notice.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteNotice(notice.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-notice-${notice.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Read Receipts Dialog (shared by Messages + Notices) */}
        <Dialog open={receiptsOpen} onOpenChange={(open) => { setReceiptsOpen(open); if (!open) setReceiptsData(null); }}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="read-receipts-dialog">
                <DialogHeader>
                  <DialogTitle>Read Receipts{receiptsData ? ` — ${receiptsData.title}` : ''}</DialogTitle>
                </DialogHeader>
                {receiptsLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
                  </div>
                ) : receiptsData && (
                  <div className="space-y-5">
                    <div className="flex gap-3">
                      <Badge className="bg-green-100 text-green-700">{receiptsData.read_count} read</Badge>
                      <Badge className="bg-red-100 text-red-700">{receiptsData.unread_count} not read</Badge>
                      <Badge variant="outline">{receiptsData.total} parents</Badge>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1">
                        <X className="w-4 h-4" /> Not read yet ({receiptsData.unread_count})
                      </h4>
                      {receiptsData.unread.length === 0 ? (
                        <p className="text-sm text-neutral-500">Everyone has read this 🎉</p>
                      ) : (
                        <div className="space-y-1.5">
                          {receiptsData.unread.map(p => (
                            <div key={p.id} className="flex items-center justify-between gap-2 bg-red-50 border border-red-100 rounded px-3 py-1.5" data-testid={`unread-parent-${p.id}`}>
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{p.name}</p>
                                <p className="text-xs text-neutral-500 truncate">{p.email}</p>
                              </div>
                              {p.whatsapp_url && (
                                <a
                                  href={p.whatsapp_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex-shrink-0 text-xs font-medium bg-green-600 hover:bg-green-700 text-white px-2.5 py-1 rounded-full"
                                  data-testid={`chase-whatsapp-${p.id}`}
                                >
                                  WhatsApp
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" /> Read ({receiptsData.read_count})
                      </h4>
                      {receiptsData.read.length === 0 ? (
                        <p className="text-sm text-neutral-500">No one has read this yet</p>
                      ) : (
                        <div className="space-y-1.5">
                          {receiptsData.read.map(p => (
                            <div key={p.id} className="flex items-center justify-between gap-2 bg-green-50 border border-green-100 rounded px-3 py-1.5" data-testid={`read-parent-${p.id}`}>
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{p.name}</p>
                                <p className="text-xs text-neutral-500 truncate">{p.email}</p>
                              </div>
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
      </div>
    </div>
  );
};

export default AdminMessaging;
